// Relatório da campanha (/campaign/:campaignId/report) no padrão Aeros
// (mockup "Relatório Campanha A"): header com breadcrumb + status + ações
// (Pausar/Retomar, Editar, Exportar XLSX), card de progresso segmentado,
// doughnut em CSS, 4 KPIs, 4 cards de detalhe e tabela de envios com
// filtros por status, busca por número e paginação. Dados: GET
// /campaigns/:id, /stats e /shipping (status/searchParam/page/pageSize).
import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import MotivoFalha from "../../components/MotivoFalha";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import EditIcon from "@material-ui/icons/Edit";
import GetAppIcon from "@material-ui/icons/GetApp";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ReplyIcon from "@material-ui/icons/Reply";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import ListAltIcon from "@material-ui/icons/ListAlt";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import EventIcon from "@material-ui/icons/Event";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import SearchIcon from "@material-ui/icons/Search";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import moment from "moment";
import MainContainer from "../../components/MainContainer";
import CampaignModal from "../../components/CampaignModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import useAerosSettingsStyles from "../../components/Settings/aerosSettingsStyles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { initialsOf, avatarColorFor } from "../Contacts/contactRules";
import { progressSegments, statusMeta, shippingStatus, formatPhone, estimateSeconds } from "../../components/CampaignModal/campaignRules";

const tc = (k, o) => i18n.t(`aeros.campaign.${k}`, o);
const GRID = "90px 1.3fr 2.4fr 1.1fr 1.1fr 0.9fr 1.4fr";

// Mantidos para quem importa daqui (export XLSX antigo e testes).
export const statusDoEnvio = (item) => (item?.failureReason ? "failed" : item?.deliveredAt ? "delivered" : "pending");
export const rotuloDoStatus = (status) => tc(`shipping.${status === "delivered" || status === "failed" ? status : "pending"}`);

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const border = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const purple = stateText(theme, "#C08BFF", "purple");
  const tone = (hex, key) => ({ color: stateText(theme, hex, key), backgroundColor: alpha(hex, 0.1) });
  return {
    pageHeader: { minHeight: 60, flexShrink: 0, display: "flex", alignItems: "center", gap: 12, padding: "0 12px", borderBottom: `1px solid ${line}` },
    crumb: { display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: theme.palette.text.secondary, background: "none", border: "none", cursor: "pointer", font: "inherit", padding: 0, "& .MuiSvgIcon-root": { fontSize: 16 } },
    slash: { color: theme.palette.aeros.textTertiary },
    pageTitle: { fontFamily: AEROS.fontDisplay, fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: theme.palette.text.primary },
    status: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, borderRadius: 99, padding: "3px 10px", whiteSpace: "nowrap", "& i": { width: 6, height: 6, borderRadius: "50%", background: "currentColor", boxShadow: "0 0 0 3px rgba(34,211,238,0.2)" } },
    info: tone(AEROS.cyan, "info"), success: tone(AEROS.success, "success"), warning: tone(AEROS.warning, "warning"), error: tone(AEROS.error, "error"), purple: tone("#C08BFF", "purple"),
    muted: { color: theme.palette.text.secondary, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.06)" },
    actions: { marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" },
    pageBody: { flex: 1, minHeight: 0, padding: "16px 12px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, "& > *": { flexShrink: 0 }, ...theme.scrollbarStyles },
    card: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 },
    progHead: { display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" },
    progTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    progSub: { fontSize: 12, color: theme.palette.text.secondary },
    progNum: { marginLeft: "auto", fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary, "& span": { color: theme.palette.text.secondary, fontWeight: 500 }, "& b": { color: stateText(theme, AEROS.cyan, "info"), fontWeight: 700 } },
    bar: { height: 10, borderRadius: 99, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.08)", display: "flex", overflow: "hidden" },
    legend: { display: "flex", gap: 18, fontSize: 11.5, color: theme.palette.text.secondary, flexWrap: "wrap", "& span": { display: "flex", alignItems: "center", gap: 6 }, "& i": { width: 8, height: 8, borderRadius: 2, display: "inline-block" } },
    grid: { display: "grid", gridTemplateColumns: "260px 1fr", gap: 14, [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" } },
    donutCard: { alignItems: "center" },
    donut: { position: "relative", width: 150, height: 150, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" },
    hole: { width: 106, height: 106, borderRadius: "50%", backgroundColor: theme.palette.aeros.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
    holeNum: { fontSize: 24, fontWeight: 700, letterSpacing: "-0.03em", color: theme.palette.text.primary },
    holeSub: { fontSize: 10.5, color: theme.palette.text.secondary },
    legendCol: { display: "flex", flexDirection: "column", gap: 4, width: "100%", fontSize: 11.5, fontVariantNumeric: "tabular-nums", color: theme.palette.text.secondary, "& div": { display: "flex", alignItems: "center", gap: 6 }, "& i": { width: 8, height: 8, borderRadius: 2, display: "inline-block" }, "& b": { marginLeft: "auto", color: theme.palette.text.primary, fontWeight: 600 } },
    kpis: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr 1fr" } },
    kpi: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "16px 18px" },
    kpiHead: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.palette.text.secondary, fontWeight: 500, "& .MuiSvgIcon-root": { fontSize: 17 } },
    kpiValue: { fontSize: 24, fontWeight: 700, letterSpacing: "-0.03em", marginTop: 8, fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary },
    kpiSub: { fontSize: 11.5, color: theme.palette.text.secondary, marginTop: 3 },
    detail: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    detailIcon: { width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flex: "none", "& .MuiSvgIcon-root": { fontSize: 18 } },
    detailLabel: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary },
    detailValue: { fontSize: 12.5, fontWeight: 600, marginTop: 2, color: theme.palette.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    tableHead: { display: "flex", alignItems: "center", padding: "12px 20px", gap: 10, borderBottom: `1px solid ${line}`, flexWrap: "wrap" },
    tableTitle: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary },
    pills: { display: "flex", gap: 6, marginLeft: 8, flexWrap: "wrap" },
    pill: { borderRadius: 99, padding: "4px 11px", fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, border: `1px solid ${border}`, background: "none", cursor: "pointer", font: "inherit", "& b": { fontWeight: 700 } },
    pillOn: { color: purple, backgroundColor: alpha("#A044FF", 0.15), borderColor: "transparent" },
    search: { marginLeft: "auto", backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, display: "flex", gap: 6, alignItems: "center", width: 220, "& input": { flex: 1, minWidth: 0, background: "none", border: "none", outline: "none", font: "inherit", color: theme.palette.text.primary, "&::placeholder": { color: theme.palette.aeros.textTertiary } }, "& .MuiSvgIcon-root": { fontSize: 15, color: theme.palette.text.secondary } },
    tableWrap: { overflowX: "auto", "& > div": { minWidth: 960 } },
    tHead: { display: "grid", gridTemplateColumns: GRID, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.02)", "& > div": { padding: "11px 8px" }, "& > div:first-child": { paddingLeft: 20 }, "& > div:last-child": { paddingRight: 20 } },
    tRow: { display: "grid", gridTemplateColumns: GRID, alignItems: "center", borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`, fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary, "&:hover": { backgroundColor: alpha("#A044FF", 0.04) }, "& > div": { padding: "10px 8px", minWidth: 0 }, "& > div:first-child": { paddingLeft: 20 }, "& > div:last-child": { paddingRight: 20 } },
    id: { color: theme.palette.text.secondary, fontWeight: 600 },
    recipient: { display: "flex", alignItems: "center", gap: 8 },
    av: { width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: "#07080D", flex: "none" },
    ell: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sub: { fontSize: 10.5, color: theme.palette.aeros.textTertiary },
    secondary: { color: theme.palette.text.secondary },
    dash: { color: theme.palette.aeros.textTertiary },
    shipPill: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap", "& .MuiSvgIcon-root": { fontSize: 13 } },
    reason: { fontSize: 11.5, color: stateText(theme, AEROS.error, "error"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    foot: { display: "flex", alignItems: "center", padding: "12px 20px", fontSize: 12, color: theme.palette.text.secondary, gap: 14 },
    pageSize: { backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 8, padding: "2px 10px", fontSize: 11.5, display: "flex", alignItems: "center" },
    sel: { fontSize: 11.5, color: theme.palette.text.primary, "& .MuiNativeSelect-select": { padding: 0, paddingRight: 18, background: "transparent", "&:focus": { background: "transparent" } }, "& .MuiNativeSelect-icon": { fontSize: 14, top: "calc(50% - 7px)", color: theme.palette.text.secondary } },
    pages: { marginLeft: "auto", display: "flex", gap: 4 },
    pg: { width: 28, height: 28, borderRadius: 8, border: `1px solid ${border}`, display: "flex", alignItems: "center", justifyContent: "center", background: "none", cursor: "pointer", font: "inherit", fontSize: 12, color: theme.palette.text.primary, "&:disabled": { opacity: 0.4, cursor: "default" } },
    pgOn: { backgroundColor: alpha("#A044FF", 0.2), color: purple, fontWeight: 700, borderColor: "transparent" },
    dots: { fontSize: 12, color: theme.palette.aeros.textTertiary, padding: "0 4px", display: "flex", alignItems: "center" },
    empty: { padding: "36px 20px", textAlign: "center", fontSize: 13, color: theme.palette.text.secondary },
  };
});

const SHIP = {
  delivered: { icon: DoneAllIcon, hex: AEROS.success, cls: "success" },
  pending: { icon: ScheduleIcon, hex: AEROS.warning, cls: "warning" },
  failed: { icon: ErrorOutlineIcon, hex: AEROS.error, cls: "error" },
  queued: { icon: HourglassEmptyIcon, hex: "#9CA3B8", cls: "muted" },
};

const CampaignReport = () => {
  const classes = useStyles();
  const aeros = useAerosSettingsStyles();
  const history = useHistory();
  const { campaignId } = useParams();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();
  const mounted = useRef(true);

  const [campaign, setCampaign] = useState({});
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({});
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // "cancel" | "restart"
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getPlanCompany(undefined, user.companyId).then((planConfigs) => {
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }).catch(() => {});
    api.get("/campaign-settings").then(({ data }) => { const s = {}; (Array.isArray(data) ? data : []).forEach((it) => { s[it.key] = it.value; }); setSettings({ messageInterval: Number(s.messageInterval) || 20, longerIntervalAfter: Number(s.longerIntervalAfter) || 20, greaterInterval: Number(s.greaterInterval) || 60 }); }).catch(() => {});
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHead = async () => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}`);
      if (mounted.current) setCampaign(data);
      const { data: st } = await api.get(`/campaigns/${campaignId}/stats`);
      if (mounted.current) setStats(st);
    } catch (err) {
      toastError(err);
    }
  };

  const loadRows = async (p = page, size = pageSize, status = filter, q = search) => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/shipping`, { params: { page: p, pageSize: size, status: status || undefined, searchParam: q || undefined } });
      if (!mounted.current) return;
      setRows(data?.shipping || []);
      setCount(Number(data?.count) || 0);
      setPage(data?.currentPage || p);
    } catch (err) {
      toastError(err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => { loadHead(); loadRows(1); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket) return undefined;
    const onCampaignEvent = (data) => {
      if (Number(data?.record?.id) !== Number(campaignId)) return;
      setCampaign(data.record);
      loadHead();
      // "shippings": a varredura de SMS sem confirmacao mudou linhas, em
      // qualquer status da campanha (inclusive ja FINALIZADA).
      if (data.action === "shippings" || data.record.status === "FINALIZADA") {
        setTimeout(() => loadRows(1), 3000);
      }
    };
    socket.on(`company-${user.companyId}-campaign`, onCampaignEvent);
    return () => socket.off(`company-${user.companyId}-campaign`, onCampaignEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, socket]);

  const seg = useMemo(() => progressSegments(stats), [stats]);
  const st = statusMeta(campaign.status);
  const eta = useMemo(() => {
    if (!campaign.status || campaign.status !== "EM_ANDAMENTO" || !seg.queued) return null;
    return moment().add(estimateSeconds(seg.queued, settings), "seconds");
  }, [campaign.status, seg.queued, settings]);
  const replied = Number(stats?.conversasIniciadas) || 0;
  const confirmed = Number(stats?.confirmed) || 0;
  const counts = { "": seg.total, delivered: seg.delivered, failed: seg.failed, pending: seg.awaiting + seg.queued };

  const applyFilter = (status) => { setFilter(status); loadRows(1, pageSize, status, search); };
  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const pageItems = useMemo(() => { const set = new Set([1, pageCount, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pageCount)); const arr = [...set].sort((a, b) => a - b); return arr.flatMap((n, i) => (i > 0 && n - arr[i - 1] > 1 ? ["…", n] : [n])); }, [page, pageCount]);

  const act = async () => {
    try {
      await api.post(`/campaigns/${campaignId}/${confirm === "cancel" ? "cancel" : "restart"}`);
      toast.success(i18n.t(confirm === "cancel" ? "campaigns.toasts.cancel" : "campaigns.toasts.restart"));
      setConfirm(null);
      loadHead();
    } catch (err) {
      toastError(err);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      let all = []; let p = 1; let more = true;
      while (more) {
        const { data } = await api.get(`/campaigns/${campaignId}/shipping`, { params: { page: p, pageSize: 1000 } });
        all = [...all, ...(data?.shipping || [])];
        more = p < (data?.totalPages || 1); p += 1;
      }
      if (!all.length) { toast.error(tc("exportEmpty")); return; }
      const rowsX = [["ID", tc("table.recipient"), tc("table.message"), tc("table.sentAt"), tc("table.deliveredAt"), tc("table.status"), tc("table.reason")]];
      all.forEach((it) => rowsX.push([it.jobId || it.id, it.number || "", it.message || "", it.createdAt ? new Date(it.createdAt).toLocaleString("pt-BR") : "", it.deliveredAt ? new Date(it.deliveredAt).toLocaleString("pt-BR") : "", tc(`shipping.${shippingStatus(it)}`), it.failureReason || ""]));
      const ws = XLSX.utils.aoa_to_sheet([[`${tc("campaigns")} · ${campaign.name}`], [new Date().toLocaleString("pt-BR")], [], ...rowsX]);
      ws["!cols"] = [{ wch: 15 }, { wch: 20 }, { wch: 50 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 50 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Campanha");
      XLSX.writeFile(wb, `relatorio_campanha_${String(campaign.name || campaignId).replace(/[^a-zA-Z0-9]/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(tc("exportOk", { count: all.length }));
    } catch (err) {
      toast.error(tc("exportError"));
    } finally {
      setExporting(false);
    }
  };

  const details = [
    { icon: SyncAltIcon, hex: AEROS.success, label: tc("detail.connection"), value: campaign.whatsapp?.name || campaign.whatsappName || (campaign.canal === "sms" ? "SMS" : "—") },
    campaign.tagListId && !campaign.contactListId
      ? { icon: LocalOfferOutlinedIcon, hex: AEROS.cyan, label: tc("detail.tag"), value: campaign.tagList?.name || `#${campaign.tagListId}` }
      : { icon: ListAltIcon, hex: AEROS.cyan, label: tc("detail.list"), value: campaign.contactList?.name ? `${campaign.contactList.name}${seg.total ? ` · ${seg.total.toLocaleString("pt-BR")}` : ""}` : "—" },
    { icon: EventIcon, hex: "#C08BFF", label: tc("detail.schedule"), value: campaign.scheduledAt ? `${datetimeToClient(campaign.scheduledAt)}${campaign.isRecurring && campaign.recurrenceType ? ` · ${tc(`recurrence.${campaign.recurrenceType}`)}` : ""}` : "—" },
    { icon: EventAvailableIcon, hex: "#9CA3B8", label: tc("detail.conclusion"), value: campaign.completedAt ? datetimeToClient(campaign.completedAt) : eta ? tc("detail.expected", { date: eta.format("DD/MM HH:mm") }) : tc("detail.notFinished") },
  ];

  const kpis = [
    { icon: SendIcon, hex: AEROS.cyan, label: tc("kpi.sent"), value: seg.sent, sub: tc("kpi.sentSub", { total: seg.total.toLocaleString("pt-BR") }) },
    { icon: PhoneIcon, hex: "#C08BFF", label: tc("kpi.unique"), value: Number(stats?.uniqueNumbers) || 0, sub: tc("kpi.uniqueSub", { dup: Math.max(0, seg.total - (Number(stats?.uniqueNumbers) || 0)) }) },
    { icon: DoneAllIcon, hex: AEROS.success, label: tc("kpi.delivered"), value: seg.delivered, sub: tc("kpi.deliveredSub", { pct: seg.sent ? Math.round((seg.delivered / seg.sent) * 100) : 0 }) },
    { icon: ReplyIcon, hex: AEROS.warning, label: tc("kpi.replied"), value: replied, sub: tc("kpi.repliedSub", { pct: seg.delivered ? Math.round((replied / seg.delivered) * 1000) / 10 : 0, confirmed }) },
  ];

  const donut = `conic-gradient(${AEROS.success} 0 ${seg.pctDelivered}%, ${AEROS.error} ${seg.pctDelivered}% ${seg.pctDelivered + seg.pctFailed}%, ${AEROS.warning} ${seg.pctDelivered + seg.pctFailed}% ${seg.pctDelivered + seg.pctFailed + seg.pctAwaiting}%, rgba(128,128,128,0.2) ${seg.pctDelivered + seg.pctFailed + seg.pctAwaiting}% 100%)`;

  return (
    <MainContainer>
      <div className={classes.pageHeader}>
        <button type="button" className={classes.crumb} onClick={() => history.push("/campaigns")}><ArrowBackIcon />{tc("campaigns")}</button>
        <span className={classes.slash}>/</span>
        <div className={classes.pageTitle}>{campaign.name || "…"}</div>
        {campaign.status && <span className={`${classes.status} ${classes[st.tone]}`}><i />{st.label}</span>}
        <div className={classes.actions}>
          {campaign.status === "EM_ANDAMENTO" && <Button className={aeros.ghostButton} style={{ color: AEROS.warning }} startIcon={<PauseCircleOutlineIcon />} onClick={() => setConfirm("cancel")}>{tc("pause")}</Button>}
          {campaign.status === "CANCELADA" && <Button className={aeros.ghostButton} startIcon={<PlayCircleOutlineIcon />} onClick={() => setConfirm("restart")}>{tc("resume")}</Button>}
          <Button className={aeros.ghostButton} startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>{i18n.t("campaigns.table.actions") === "Ações" ? "Editar" : i18n.t("aeros.contacts.edit")}</Button>
          <Button className={aeros.gradientButton} startIcon={exporting ? <CircularProgress size={14} /> : <GetAppIcon />} disabled={exporting} onClick={exportToExcel}>{tc("export")}</Button>
        </div>
      </div>

      <div className={classes.pageBody}>
        <div className={classes.card}>
          <div className={classes.progHead}>
            <div className={classes.progTitle}>{tc("progress")}</div>
            <div className={classes.progSub}>{campaign.lastExecutedAt || campaign.scheduledAt ? tc("startedAt", { date: moment(campaign.lastExecutedAt || campaign.scheduledAt).format("DD/MM HH:mm") }) : ""}{eta ? ` · ${tc("eta", { date: eta.format("DD/MM HH:mm") })}` : ""}</div>
            <div className={classes.progNum}>{seg.sent.toLocaleString("pt-BR")} <span>{tc("of")} {seg.total.toLocaleString("pt-BR")}</span> <b>· {seg.pctSent}%</b></div>
          </div>
          <div className={classes.bar}>
            <div style={{ width: `${seg.pctDelivered}%`, background: AEROS.success }} />
            <div style={{ width: `${seg.pctFailed}%`, background: AEROS.error }} />
            <div style={{ width: `${seg.pctAwaiting}%`, background: AEROS.warning }} />
          </div>
          <div className={classes.legend}>
            <span><i style={{ background: AEROS.success }} />{tc("delivered")} {seg.delivered}</span>
            <span><i style={{ background: AEROS.error }} />{tc("failed")} {seg.failed}</span>
            <span><i style={{ background: AEROS.warning }} />{tc("awaiting")} {seg.awaiting}</span>
            <span><i style={{ background: "rgba(128,128,128,0.35)" }} />{tc("queued")} {seg.queued}</span>
          </div>
        </div>

        <div className={classes.grid}>
          <div className={`${classes.card} ${classes.donutCard}`}>
            <div className={classes.kpiHead} style={{ alignSelf: "flex-start" }}>{tc("deliveryStatus")}</div>
            <div className={classes.donut} style={{ background: donut }}>
              <div className={classes.hole}><div className={classes.holeNum}>{seg.deliveryRate}%</div><div className={classes.holeSub}>{tc("deliveredPct")}</div></div>
            </div>
            <div className={classes.legendCol}>
              <div><i style={{ background: AEROS.success }} />{tc("delivered")}<b>{seg.delivered}</b></div>
              <div><i style={{ background: AEROS.error }} />{tc("failed")}<b>{seg.failed}</b></div>
              <div><i style={{ background: AEROS.warning }} />{tc("awaiting")}<b>{seg.awaiting}</b></div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
            <div className={classes.kpis}>
              {kpis.map((k) => { const Icon = k.icon; return (
                <div key={k.label} className={classes.kpi}>
                  <div className={classes.kpiHead}><Icon style={{ color: k.hex }} />{k.label}</div>
                  <div className={classes.kpiValue}>{Number(k.value).toLocaleString("pt-BR")}</div>
                  <div className={classes.kpiSub}>{k.sub}</div>
                </div>
              ); })}
            </div>
            <div className={classes.kpis}>
              {details.map((d) => { const Icon = d.icon; return (
                <div key={d.label} className={classes.detail}>
                  <div className={classes.detailIcon} style={{ backgroundColor: alpha(d.hex, 0.12), color: d.hex }}><Icon /></div>
                  <div style={{ minWidth: 0 }}><div className={classes.detailLabel}>{d.label}</div><div className={classes.detailValue} title={d.value}>{d.value}</div></div>
                </div>
              ); })}
            </div>
          </div>
        </div>

        <div className={aeros.tableCard}>
          <div className={classes.tableHead}>
            <div className={classes.tableTitle}>{tc("table.title")}</div>
            <div className={classes.pills}>
              {[["", "table.all", null], ["delivered", "delivered", AEROS.success], ["failed", "failed", AEROS.error], ["pending", "pending", AEROS.warning]].map(([k, label, hex]) => (
                <button key={k || "all"} type="button" className={`${classes.pill} ${filter === k ? classes.pillOn : ""}`} onClick={() => applyFilter(k)}>{tc(label)} <b style={{ color: filter === k ? undefined : hex || undefined }}>{(counts[k] || 0).toLocaleString("pt-BR")}</b></button>
              ))}
            </div>
            <form className={classes.search} onSubmit={(e) => { e.preventDefault(); loadRows(1, pageSize, filter, search); }}>
              <SearchIcon /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tc("table.search")} aria-label={tc("table.search")} />
            </form>
          </div>
          <div className={classes.tableWrap}>
            <div>
              <div className={classes.tHead}>
                {["id", "recipient", "message", "sentAt", "deliveredAt", "status", "reason"].map((k) => <div key={k}>{tc(`table.${k}`)}</div>)}
              </div>
              {loading ? <div className={classes.empty}><CircularProgress size={22} /></div>
              : !rows.length ? <div className={classes.empty}>{tc("table.empty")}</div>
              : rows.map((r) => {
                const s = shippingStatus(r); const S = SHIP[s]; const Icon = S.icon;
                const name = r.contact?.name || r.contactName || formatPhone(r.number);
                return (
                  <div key={r.id} className={classes.tRow}>
                    <div className={classes.id}>{r.jobId ? String(r.jobId).slice(-6) : r.id}</div>
                    <div className={classes.recipient}>
                      <div className={classes.av} style={{ backgroundColor: avatarColorFor(name || "") }}>{initialsOf(name || "?")}</div>
                      <div style={{ minWidth: 0 }}><div className={`${classes.ell}`} style={{ fontWeight: 600 }}>{name}</div><div className={classes.sub}>{formatPhone(r.number)}</div></div>
                    </div>
                    <div className={`${classes.secondary} ${classes.ell}`} title={r.message}>{r.message}</div>
                    <div className={classes.secondary}>{r.createdAt ? moment(r.createdAt).format("DD/MM HH:mm") : <span className={classes.dash}>—</span>}</div>
                    <div className={r.deliveredAt ? classes.secondary : classes.dash}>{r.deliveredAt ? moment(r.deliveredAt).format("DD/MM HH:mm") : "—"}</div>
                    <div><span className={`${classes.shipPill} ${classes[S.cls]}`}><Icon />{tc(`shipping.${s}`)}</span></div>
                    <div className={r.failureReason ? classes.reason : classes.dash} style={r.failureReason ? { whiteSpace: "normal", overflow: "visible", textOverflow: "clip" } : undefined}>{r.failureReason ? <MotivoFalha reason={r.failureReason} /> : "—"}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes.foot}>
            <div>{count ? tc("table.range", { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, count), total: count.toLocaleString("pt-BR") }) : ""}</div>
            <div className={classes.pageSize}>
              <Select native disableUnderline className={classes.sel} value={pageSize} onChange={(e) => { const n = Number(e.target.value); setPageSize(n); loadRows(1, n, filter, search); }} inputProps={{ "aria-label": i18n.t("aeros.reports.perPageAria") }}>
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{i18n.t("aeros.reports.perPage", { n })}</option>)}
              </Select>
            </div>
            <div className={classes.pages}>
              <button type="button" className={classes.pg} disabled={page <= 1} onClick={() => loadRows(page - 1, pageSize, filter, search)}>‹</button>
              {pageItems.map((n, i) => (n === "…" ? <span key={`d${i}`} className={classes.dots}>…</span> : <button key={n} type="button" className={`${classes.pg} ${n === page ? classes.pgOn : ""}`} onClick={() => loadRows(n, pageSize, filter, search)}>{n}</button>))}
              <button type="button" className={classes.pg} disabled={page >= pageCount} onClick={() => loadRows(page + 1, pageSize, filter, search)}>›</button>
            </div>
          </div>
        </div>
      </div>

      {editOpen && <CampaignModal open onClose={() => { setEditOpen(false); loadHead(); }} campaignId={Number(campaignId)} resetPagination={() => loadRows(1)} />}
      <AerosConfirmModal
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={act}
        icon={confirm === "cancel" ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
        tone={confirm === "cancel" ? "warning" : "info"}
        title={confirm === "cancel" ? tc("confirmCancel.title") : tc("resume")}
        description={confirm === "cancel" ? tc("confirmCancel.text", { name: campaign.name, delivered: seg.delivered, total: seg.total, remaining: seg.queued + seg.awaiting }) : `${campaign.name}`}
        confirmLabel={confirm === "cancel" ? tc("confirmCancel.confirm") : tc("resume")}
        cancelLabel={confirm === "cancel" ? tc("confirmCancel.keep") : tc("cancel")}
      />
    </MainContainer>
  );
};

export default CampaignReport;
